import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { History, XCircle } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { useTaxRule, useTaxRules } from '@/features/tax-rules/hooks/useTaxRules'
import type { TaxRule } from '@/features/tax-rules/types/tax-rule.types'

function rateDisplay(rule: TaxRule): string {
  if (rule.ruleType === 'flat_rate' || rule.ruleType === 'percentage') {
    return rule.rate != null ? `${rule.rate.toFixed(2)}%` : '—'
  }
  if (rule.ruleType === 'fixed_amount') {
    return rule.fixedAmount != null ? rule.fixedAmount.toLocaleString() : '—'
  }
  if (rule.ruleType === 'progressive_brackets') {
    return `${rule.brackets.length} bracket(s)`
  }
  if (rule.ruleType === 'salary_range') {
    return `${rule.brackets.length} slab(s)`
  }
  return '—'
}

export function TaxRuleVersionHistoryPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data: currentRule, isLoading: ruleLoading } = useTaxRule(id ?? null)

  // Fetch all rules and filter to version chain client-side
  // The chain: current rule's parentRuleId points to the "family" root
  const rootId = currentRule?.parentRuleId ?? id
  const { data: allRules, isLoading: listLoading } = useTaxRules({ page: 1, limit: 100 })

  const isLoading = ruleLoading || listLoading

  // Build version chain: all rules sharing the same parentRuleId (or being the root)
  const versions: TaxRule[] = []
  if (allRules?.data && rootId) {
    for (const r of allRules.data) {
      if (r._id === rootId || r.parentRuleId === rootId) {
        versions.push(r)
      }
    }
    // Also add current if it's the root itself
    if (currentRule && !versions.find((v) => v._id === currentRule._id)) {
      versions.push(currentRule)
    }
    versions.sort((a, b) => b.version - a.version)
  }

  const selected = selectedId ? (versions.find((v) => v._id === selectedId) ?? null) : null

  if (isLoading) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'Tax Rules', to: '/tax-rules' }, { label: 'Version History' }]}
        icon={History}
        title="Version History"
        className="gap-0"
        hideHeader
      >
        <TableSkeleton rows={4} cols={3} />
      </PageShell>
    )
  }

  if (!currentRule) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'Tax Rules', to: '/tax-rules' }, { label: 'Not Found' }]}
        icon={History}
        title="Version History"
        className="gap-0"
        hideHeader
      >
        <EmptyState icon={XCircle} title="Tax rule not found" />
      </PageShell>
    )
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Tax Rules', to: '/tax-rules' },
        { label: currentRule.name, to: `/tax-rules/${id}` },
        { label: 'History' },
      ]}
      icon={History}
      title={`Version History — ${currentRule.name}`}
      description={`${currentRule.country} · ${versions.length} version(s)`}
      className="gap-0"
      hideHeader
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Timeline */}
        <div className="lg:col-span-1">
          {versions.length <= 1 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This is the original version. No previous versions exist.
            </p>
          ) : (
            <div className="relative border-l-2 border-gray-200 pl-6 dark:border-gray-700">
              {versions.map((v) => {
                const isCurrent = v._id === id
                const isSelected = v._id === selectedId
                return (
                  <button
                    key={v._id}
                    type="button"
                    onClick={() => setSelectedId(v._id)}
                    className={`relative mb-4 block w-full text-left last:mb-0 ${
                      isSelected ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                    }`}
                  >
                    {/* Dot */}
                    <div
                      className={`absolute -left-[31px] top-1 h-4 w-4 rounded-full border-2 ${
                        v.isActive
                          ? 'border-green-500 bg-green-100 dark:bg-green-900/50'
                          : 'border-gray-400 bg-gray-100 dark:bg-gray-700'
                      }`}
                    />
                    <div className="text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          v{v.version}
                        </span>
                        {v.isActive && <Badge variant="success">Active</Badge>}
                        {isCurrent && <Badge variant="info">Current</Badge>}
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        From: {new Date(v.effectiveFrom).toLocaleDateString()}
                        {v.effectiveTo && ` — To: ${new Date(v.effectiveTo).toLocaleDateString()}`}
                      </p>
                      <p className="text-xs text-gray-400">
                        Created {new Date(v.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="rounded-lg border border-gray-200 p-5 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selected.name} — v{selected.version}
              </h3>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Country</span>
                  <span className="font-mono">{selected.country}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Type</span>
                  <Badge variant="info">{selected.ruleType.replace('_', ' ')}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Rate / Value</span>
                  <span>{rateDisplay(selected)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Applies To</span>
                  <span>{selected.appliesTo.join(', ') || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Active</span>
                  <Badge variant={selected.isActive ? 'success' : 'neutral'}>
                    {selected.isActive ? 'Yes' : 'No'}
                  </Badge>
                </div>
                {selected.brackets.length > 0 && (
                  <div className="pt-2">
                    <p className="mb-1 text-xs font-medium uppercase text-gray-500">
                      {selected.ruleType === 'salary_range' ? 'Salary Slabs' : 'Brackets'}
                    </p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="py-1">Min</th>
                          <th className="py-1">Max</th>
                          <th className="py-1">Rate</th>
                          <th className="py-1">Fixed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.brackets.map((b, i) => (
                          <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                            <td className="py-1 tabular-nums">{b.min.toLocaleString()}</td>
                            <td className="py-1 tabular-nums">
                              {b.max == null ? '∞' : b.max.toLocaleString()}
                            </td>
                            <td className="py-1 tabular-nums">{b.rate.toFixed(2)}%</td>
                            <td className="py-1 tabular-nums">{b.fixedComponent ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {selected.exemptions.length > 0 && (
                  <div className="pt-2">
                    <p className="mb-1 text-xs font-medium uppercase text-gray-500">Exemptions</p>
                    {selected.exemptions.map((ex, i) => (
                      <div key={i} className="text-xs">
                        {ex.type}: {ex.amount.toLocaleString()}
                        {ex.description && ` — ${ex.description}`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-gray-400">
              Select a version to view details
            </div>
          )}
        </div>
      </div>
    </PageShell>
  )
}

TaxRuleVersionHistoryPage.displayName = 'TaxRuleVersionHistoryPage'
